import { line as d3Line } from 'd3-shape';
import { ISample } from '../models/ichannel';
import { ECG_CONSTANTS } from '../utils/ecg-constants';
import { Scaler } from '../utils/scaler';
import {
    ICaliperViewModel,
    IFiducialPoint,
    ILayout,
    IStepViewModel,
    ITimeRange,
    ITitleViewModel,
    ITraceData,
    ITraceViewModel,
} from '../models/interfaces';
import { ChannelSelector } from './channel-selector';
import { Appearance } from './appearance';
import { FiducialPoint } from './fiducial-point';
import { TitleViewModel } from './title-view-model';
import { StepViewModel } from './step-viewmodel';

export class TraceViewModel implements ITraceViewModel {
    // data members

    title: ITitleViewModel | null = null;
    step: IStepViewModel | null = null;
    fiducialPoints = new Array<IFiducialPoint>();
    layout: ILayout | null = null;
    appearance: Appearance = new Appearance();
    samples: string = '';
    translate: string = '';
    visible: boolean = false;
    selected: boolean = false;
    scaler: any | null = null;
    scaleLine: any | null = null;
    range: ITimeRange = { start: 0, end: 10000 };
    private _originalFiducialPoints: number[] = [];

    constructor(layout: ILayout, traceData: ITraceData) {
        if (!layout.context) return;
        this._channelSelector = new ChannelSelector(traceData.originalChannel, traceData.derivedChannel, layout);

        this.layout = layout;

        this._originalFiducialPoints = traceData.markers;

        this._windowedSamples = this.channel?.samples ?? [];

        this.updateScale();

        this.title = new TitleViewModel(this.channel?.name ?? '');
        this.step = new StepViewModel(layout.context, this.channel);

        this.updateTrace();
    }

    private _calipers = new Array<ICaliperViewModel>();

    get calipers() {
        return this._calipers;
    }

    get channel() {
        return this.channelSelector?.currentChannel ?? null;
    }

    private _channelSelector: ChannelSelector | null = null;

    get channelSelector() {
        return this._channelSelector;
    }

    protected _windowedSamples = new Array<ISample>();

    // constructor

    get windowedSamples() {
        return this._windowedSamples;
    }

    // public functions

    updateTraceTimeAlignment(range: ITimeRange) {
        this.range = range;

        this._windowedSamples = this.channel?.samples.slice(range.start, range.end) ?? [];

        this.updateTrace();
    }

    updateTrace() {
        const leadHeight = this.layout?.context?.leadHeight ?? 0;
        this.scaleLine = this.updateScale();
        this.samples = this.scaleLine(this.windowedSamples) ?? '';

        this.fiducialPoints = new Array<IFiducialPoint>();

        for (const fiducialPoint of this._originalFiducialPoints) {
            if (!!this.scaler && fiducialPoint >= this.range.start && fiducialPoint <= this.range.end) {
                this.fiducialPoints.push(new FiducialPoint(fiducialPoint - this.range.start, this.scaler, leadHeight));
            }
        }

        this.fiducialPoints.forEach((fiducialPoint) => fiducialPoint.update());
    }

    // protected functions

    protected updateScale() {
        const leadWidth = this.layout?.context?.leadWidth ?? 0;
        const leadHeight = this.layout?.context?.leadHeight ?? 0;
        const scaleSettingsX = {
            domain: { min: 0, max: this.windowedSamples.length },
            range: { min: 0, max: leadWidth },
        };
        const scaleSettingsY = {
            domain: {
                min: -ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE,
                max: ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE,
            },
            range: { min: leadHeight, max: 0 },
        };
        this.scaler = new Scaler(scaleSettingsX, scaleSettingsY);
        const scaler = this.scaler;
        let line = d3Line<ISample>()
            .x((_sample: ISample, index) => {
                return scaler.onX(index);
            })
            .y((sample: ISample) => {
                return scaler.onY(sample.value);
            });

        return line;
    }
}
